import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  MarketActionCard,
  MarketBanner,
  MarketButton,
  MarketList,
  MarketRow,
} from '@market/react';
import { Box, Grid, useTheme } from '@mui/material';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useOrderDetails } from 'components/context/order_details';
import PageLoader from 'components/common/page_loader';
import {
  onCheckoutPageClick,
  onCheckoutPageView,
  onCheckoutPageViewV2,
  pushHistoryWithSearchParams,
  replaceHistoryWithSearchParams,
} from 'components/pages/checkout/utilities';
import { toCurrency } from 'utilities/currency';
import {
  VERIFY_IDENTITY,
  buildExtBankAcctFaqLink,
  buildSignInLink,
  buildSignUpLink,
} from 'utilities/links';
import { INELIGIBILITY_REASONS } from 'constants/financing';
import {
  isDashboardMode,
  isSourceQsrHardwareFlow,
} from 'helpers/search_parameter_helpers';
import DashboardModeFooter from 'components/common/dashboard_mode_footer';
import { shopFooterDisclaimers } from 'utilities/disclaimers';
import { COMPONENT_NAMES } from 'constants/tracking';
import { SEARCH_PARAM_NAMES } from 'constants/enums';
import { useUserJourneys } from 'components/context/user_journeys';
import {
  FrustrationSignal,
  UserJourneyName,
  UserJourneyVariantKeyByValue,
} from 'constants/user_journeys';

// A query param to indicate a user signed in/up after clicking the links
// presented on this page. Also used by `CheckoutV2Controller#prevent_middle_access`.
export const INSTALLMENTS_LOGIN_QUERY_PARAM = 'installments_login';
const installmentsLoginReturn = new URLSearchParams(window.location.search).has(
  INSTALLMENTS_LOGIN_QUERY_PARAM,
);

const PaymentOption = () => {
  const theme = useTheme();
  const history = useHistory();
  const { t } = useTranslation();

  const { trackingData } = useOrderDetails();
  const { checkoutClient, orderData, orderRefresh } = useCheckoutFlow();
  const { userJourneyTracker } = useUserJourneys();

  const [processing, setProcessing] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState('');

  const removeInstallmentQueryParam = () => {
    const urlSearchParams = new URLSearchParams(history.location.search);

    if (urlSearchParams.has(INSTALLMENTS_LOGIN_QUERY_PARAM)) {
      urlSearchParams.delete(INSTALLMENTS_LOGIN_QUERY_PARAM);
      history.replace(`${history.location.pathname}?${urlSearchParams}`);
    }
  };

  const fetchPaymentOptions = () => {
    checkoutClient
      .fetchPaymentOptions()
      .then((data) => {
        const options = data.payment_options;
        setPaymentOptions(options);
        setProcessing(false);
      })
      .catch(() => {
        userJourneyTracker.addFrustrationSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frustrationSignal:
            FrustrationSignal.PaymentOptionStepPaymentOptionsFetchError,
        });

        pushHistoryWithSearchParams(history, '/information');
      });
  };

  useEffect(() => {
    fetchPaymentOptions();
    removeInstallmentQueryParam();
    onCheckoutPageView('Payment Option', trackingData);
    onCheckoutPageViewV2({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT.PAYMENT_METHOD,
      additionalParameters: trackingData,
    });
  }, []);

  useEffect(() => {
    if (paymentOptions.length === 0) {
      return;
    }

    if (paymentOptions.length === 1) {
      userJourneyTracker.setVariant({
        userJourneyName: UserJourneyName.ShopPlaceOrder,
        variant: UserJourneyVariantKeyByValue(paymentOptions[0].name),
      });

      replaceHistoryWithSearchParams(history, '/payment');
      return;
    }

    let selected = paymentOptions.find((o) => o.selected).name;
    const installmentOption = paymentOptions.find(
      (o) => o.name === 'installment',
    );

    if (
      installmentsLoginReturn &&
      installmentOption &&
      installmentOption.ineligibility_reasons.length === 0 &&
      orderData.current_user?.is_logged_in
    ) {
      selected = installmentOption.name;
    }

    userJourneyTracker.setVariant({
      userJourneyName: UserJourneyName.ShopPlaceOrder,
      variant: UserJourneyVariantKeyByValue(selected),
    });
    setSelectedOption(selected);
  }, [paymentOptions]);

  const selectPaymentOption = (paymentOption) => {
    checkoutClient
      .selectPaymentOption(paymentOption)
      .then(() => {
        setProcessing(false);
        orderRefresh();

        userJourneyTracker.setVariant({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          variant: UserJourneyVariantKeyByValue(paymentOption),
        });
      })
      .catch(() => {
        userJourneyTracker.addFrustrationSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frustrationSignal:
            FrustrationSignal.PaymentOptionStepPaymentSelectError,
        });

        fetchPaymentOptions();
      });
  };

  const selectOffer = (token) => {
    checkoutClient
      .selectOffer(token)
      .then(() => {
        setProcessing(false);
        orderRefresh();
      })
      .catch(() => {
        userJourneyTracker.addFrustrationSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frustrationSignal:
            FrustrationSignal.PaymentOptionStepOfferSelectError,
        });

        fetchPaymentOptions();
      });
  };

  const onChange = (newValue) => {
    if (newValue === null) {
      return;
    }

    setProcessing(true);
    setSelectedOption(newValue);

    if (newValue === 'installment') {
      // find the installment option to check for token
      const installmentOption = paymentOptions.find(
        (o) => o.name === 'installment',
      );
      // if token is null it means the customer is going through checkout flow for the first time
      // so we cannot do anything until they proceed to offers page and actually select something
      installmentOption.token === null
        ? setProcessing(false)
        : selectOffer(installmentOption.token);
    } else {
      selectPaymentOption(newValue);
    }
  };

  const onSubmit = () => {
    setProcessing(true);
    if (selectedOption === 'installment') {
      onCheckoutPageClick({
        eventDesc: 'Continue to offer select',
        componentName: COMPONENT_NAMES.APPS.CHECKOUT.PAYMENT_METHOD,
        actionItem: 'continue_to_offer_select',
        trackingData,
      });

      pushHistoryWithSearchParams(history, '/offer_select');
    } else {
      onCheckoutPageClick({
        eventDesc: 'Continue to payment information',
        componentName: COMPONENT_NAMES.APPS.CHECKOUT.PAYMENT_METHOD,
        actionItem: 'continue_to_payment_information',
        trackingData,
      });

      pushHistoryWithSearchParams(history, '/payment');
    }
  };

  if (paymentOptions.length === 0) {
    return <PageLoader />;
  }

  const installmentReturnParams = (isGoldenPath) => {
    const params = new URLSearchParams(window.location.search);
    params.set(INSTALLMENTS_LOGIN_QUERY_PARAM, '1');
    if (isGoldenPath) {
      params.set(SEARCH_PARAM_NAMES.INITIAL_PROGRESS, 85);
    }

    return encodeURIComponent(`?${params.toString()}`);
  };

  const installmentSignInLink = `${buildSignInLink({
    returnTo: true,
  })}${installmentReturnParams()}`;

  const installmentSignUpLink = `${buildSignUpLink({
    returnTo: true,
  })}${installmentReturnParams()}`;

  const installmentIdvLink = `${buildSignUpLink({
    returnTo: true,
    step: VERIFY_IDENTITY,
  })}${installmentReturnParams()}`;

  const goldenPathIdvLink = `${buildSignUpLink({
    returnTo: true,
    step: VERIFY_IDENTITY,
    isGoldenPath: true,
  })}${installmentReturnParams(true)}`;

  const externalBankAccountFaqLink = buildExtBankAcctFaqLink();

  const optionRows = paymentOptions.map((o) => {
    const cost = toCurrency(o.price.amount, o.price.currency);
    const ineligibility_reasons = o.ineligibility_reasons ?? [];
    const period = o.number_payment_periods;

    const disableInstallmentOption =
      o.name === 'installment' && ineligibility_reasons.length > 0;
    const showInstallmentLoginPrompt =
      disableInstallmentOption &&
      ineligibility_reasons.includes(INELIGIBILITY_REASONS.LOGIN_REQUIRED);
    const showExternalBankAcctNotice =
      disableInstallmentOption &&
      ineligibility_reasons.includes(
        INELIGIBILITY_REASONS.EXTERNAL_BANK_ACCOUNT_REQUIRED,
      );
    const showIdvPrompt =
      orderData.current_user?.is_logged_in &&
      disableInstallmentOption &&
      ineligibility_reasons.includes(
        INELIGIBILITY_REASONS.IDENTITY_VERIFICATION_REQUIRED,
      );

    const isHardwareRentalUpfront =
      orderData?.is_hardware_rental_order && o.name === 'upfront';

    return (
      <React.Fragment key={o.name}>
        <MarketActionCard
          {...(selectedOption === o.name && { selected: true })}
          {...(disableInstallmentOption && { disabled: true })}
          key={o.name}
          value={o.name}
        >
          <MarketRow>
            <label slot="label">
              {isHardwareRentalUpfront
                ? t('checkout.payment_option.option_name.hardware_rental')
                : t(`checkout.payment_option.option_name.${o.name}`)}
            </label>
            {!isHardwareRentalUpfront && (
              <label slot="side-label">
                {o.name === 'installment'
                  ? t('checkout.payment_option.installment_tag', {
                      cost,
                      period,
                    })
                  : cost}
              </label>
            )}
            {isHardwareRentalUpfront && (
              <p slot="subtext" className="paragraph-20">
                {t('checkout.payment_option.hardware_rental_subtext')}
              </p>
            )}
          </MarketRow>
        </MarketActionCard>
        {showInstallmentLoginPrompt && (
          <MarketBanner
            variant="warning"
            dismissable={false}
            style={{ marginBottom: theme.spacing(2) }}
          >
            <Trans
              className="paragraph-30"
              i18nKey="checkout.label.financingLoginMessage"
            >
              <a href={installmentSignInLink} />
              <a href={installmentSignUpLink} />
            </Trans>
          </MarketBanner>
        )}
        {showExternalBankAcctNotice && (
          <MarketBanner
            variant="warning"
            dismissible={false}
            style={{ marginBottom: theme.spacing(2) }}
          >
            <Trans
              className="paragraph-30"
              i18nKey="checkout.label.financingExternalBankAccountRequired"
            >
              <a
                href={externalBankAccountFaqLink}
                target="_blank"
                rel="noreferrer"
              />
            </Trans>
          </MarketBanner>
        )}
        {showIdvPrompt && (
          <MarketBanner
            variant="warning"
            dismissable={false}
            style={{ marginBottom: theme.spacing(2) }}
          >
            <Trans
              className="paragraph-30"
              i18nKey="checkout.label.financingIdvMessage"
            >
              <a
                href={
                  isSourceQsrHardwareFlow()
                    ? goldenPathIdvLink
                    : installmentIdvLink
                }
              />
            </Trans>
          </MarketBanner>
        )}
      </React.Fragment>
    );
  });

  return (
    <>
      <h2>{t('checkout.payment_option.title')}</h2>
      {orderData?.is_hardware_rental_order ? (
        <Box sx={{ mb: 3 }} />
      ) : (
        <p style={{ margin: theme.spacing(1, 0) }}>
          {t('checkout.payment_option.subtitle')}
        </p>
      )}
      <MarketList
        {...(processing && { disabled: true })}
        interactive
        onMarketListSelectionsDidChange={(event) =>
          onChange(event.detail.newSelectionValue)
        }
      >
        {optionRows}
      </MarketList>
      <Grid container justifyContent="flex-end">
        <Grid item xs={12} md={6}>
          <MarketButton
            {...(processing && { disabled: true })}
            id="plan-or-payment-btn"
            rank="primary"
            onClick={onSubmit}
            style={{ width: '100%' }}
          >
            {selectedOption === 'installment'
              ? t('checkout.label.seePlanOptions')
              : t('checkout.label.continueToPayment')}
          </MarketButton>
        </Grid>
      </Grid>
      {isDashboardMode() && (
        <DashboardModeFooter disclaimers={shopFooterDisclaimers()} />
      )}
    </>
  );
};

export default PaymentOption;
